import React, { useEffect, useMemo, useState } from "react";
import { Alert, Title } from "@gjensidige/builders-components";
import {
  IEnquiryAlertProps,
  IPricingQuestion,
  IPricingStepConfig,
} from "../../interfaces/EnquiryInterface";
import { useAppSelector } from "../../hooks/appHooks";

export const EnquiryAlert: React.FC<IEnquiryAlertProps> = (
  props: IEnquiryAlertProps
) => {
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<"error" | "success" | "informative" | "warning" | undefined>(undefined);
  const [variant, setVariant] = useState<"filled" | "outlined" | undefined>(undefined);

  const selectedValue = useAppSelector(
    (state) =>
      state.insurance.pricingQuestions
        .flatMap((item: IPricingStepConfig) => item.questions)
        .find((question: IPricingQuestion) => question.APIKey === props.questionKey)
        ?.selectedValue
  );

  const shouldShowAlert = useMemo(() => {
    if (selectedValue && props.conditions && props.isEnabled) {
      return props.conditions.some((condition) => {
        if (condition.operator === "greaterThan") {
          return Number(selectedValue) > condition.value;
        }
        // Add more operators as needed
        return false;
      });
    }
    return false;
  }, [selectedValue, props.conditions]);

  useEffect(() => {
    if (shouldShowAlert && props.conditions) {
      const condition = props.conditions.find((condition) => {
        switch (condition.operator) {
          case "greaterThan":
            return Number(selectedValue) > condition.value;
          case "lessThan":
            return Number(selectedValue) < condition.value;
          case "equalTo":
            return Number(selectedValue) === condition.value;
          case "notEqualTo":
            return Number(selectedValue) !== condition.value;
          // Add more operators as needed
          default:
            return false;
        }
      });

      if (condition) {
        setMessage(condition.message);
        setSeverity(condition.severity as "error" | "success" | "informative" | "warning");
        setVariant(condition.variant as "filled" | "outlined");
      }
    }
  }, [shouldShowAlert, props.conditions, selectedValue]);

  return (
    <>
      {shouldShowAlert && (
        <div className="mt-5">
          <Alert severity={severity} variant={variant}>
            <Title>{props.header}</Title>
            {message}
          </Alert>
        </div>
      )}
    </>
  );
};
